import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Login from './Login';
import Dashboard from './pages/admin/Dashboard';
import LecturerDashboard from './pages/Lecturer/Dashboard';
import LecturerCorrection from './pages/Lecturer/Correction';
import Attendance from './pages/Lecturer/Attendance';
import MyFeedback from './pages/Lecturer/MyFeedback';
import SupportTask from './pages/Lecturer/SupportTask';
import LecturerProfile from './pages/Lecturer/Profile';
import LecturerUnavailability from './pages/Lecturer/Unavailability';
import Students from './pages/admin/Students';
import MyCourseDue from './pages/admin/MyCourseDue';
import InactivatedInquiry from './pages/admin/InactivatedInquiry';
import InactivatedInquiryByAssignee from './pages/admin/InactivatedInquiryByAssignee';
import StudentsWithoutRegistration from './pages/admin/StudentsWithoutRegistration';
import UploadStudents from './pages/admin/UploadStudents';
import UploadUniversityRegistrations from './pages/admin/UploadUniversityRegistrations';
import UploadPathwayStreams from './pages/admin/UploadPathwayStreams';
import UploadStaffCourseMappings from './pages/admin/UploadStaffCourseMappings';
import Correction from './pages/admin/Correction';
import Problems from './pages/admin/Problems';
import Contacts from './pages/admin/Contacts';
import TodoList from './pages/admin/TodoList';
import StudentIssue from './pages/admin/StudentIssue';
import Remider from './pages/admin/Remider';
import StickyNote from './pages/admin/StickyNote';
import Courses from './pages/admin/Courses';
import Delivery from './pages/admin/Delivery';
import AcademicCalendar from './pages/admin/AcademicCalendar';
import HndExamBoard from './pages/admin/HndExamBoard';
import TopupExamBoard from './pages/admin/TopupExamBoard';
import NotCorrectedAssignmentList from './pages/admin/NotCorrectedAssignmentList';
import HndAssignmentTracking from './pages/admin/HndAssignmentTracking';
import TopupAssignmentTracking from './pages/admin/TopupAssignmentTracking';
import Staff from './pages/admin/Staff';
import DueCourseMapping from './pages/admin/DueCourseMapping';
import StaffUnavailability from './pages/admin/StaffUnavailability';
import CourseMapping from './pages/admin/CourseMapping';
import Timetable from './pages/admin/Timetable';
import AttendanceTracking from './pages/admin/AttendanceTracking';
import LectureHours from './pages/admin/LectureHours';
import StudentsAttendance from './pages/admin/StudentsAttendance';
import Positions from './pages/admin/Positions';
import Subjects from './pages/admin/Subjects';
import AssignSubject from './pages/admin/AssignSubject';
import Classrooms from './pages/admin/Classrooms';
import Batches from './pages/admin/Batches';
import BatchGroups from './pages/admin/BatchGroups';
import ElmsModuleCode from './pages/admin/ElmsModuleCode';
import Profile from './pages/admin/Profile';
import SalesDashboard from './pages/Sales/Dashboard';
import SalesInquiry from './pages/Sales/Inquiry';
import SalesTarget from './pages/Sales/Target';
import SalesCourseInfo from './pages/Sales/CourseInfo';
import SalesMyWork from './pages/Sales/MyWork';
import SalesOfferLetter from './pages/Sales/OfferLetter';
import SalesPerformance from './pages/Sales/Performance';
import SalesProfile from './pages/Sales/Profile';
import AdminSalesDashboard from './pages/AdminSales/Dashboard';
import AdminSalesInquiries from './pages/AdminSales/Inquiries';
import AdminSalesCourseCode from './pages/AdminSales/CourseCode';
import AdminSalesStaffCourseMapping from './pages/AdminSales/StaffCourseMapping';
import AdminSalesTargetSummary from './pages/AdminSales/TargetSummary';
import AdminSalesCourseCategory from './pages/AdminSales/CourseCategory';
import AdminSalesAnnualTarget from './pages/AdminSales/AnnualTarget';
import AdminSalesBranchMonthlyTarget from './pages/AdminSales/BranchMonthlyTarget';
import AdminSalesBranchNewIncome from './pages/AdminSales/BranchNewIncome';
import AdminSalesBranchNewIncomeAnalysis from './pages/AdminSales/BranchNewIncomeAnalysis';
import RecoveryDashboard from './pages/Recovery/Dashboard';
import RecoveryArrears from './pages/Recovery/Arrears';
import RecoveryPayments from './pages/Recovery/Payments';
import RecoveryDuesList from './pages/Recovery/DuesList';
import RecoveryUploadDues from './pages/Recovery/UploadDues';
import RecoveryProfile from './pages/Recovery/Profile';
import InactivatedInquiryFollowup from './pages/Recovery/InactivatedInquiryFollowup';
import StudentDetailReport from './pages/admin/StudentDetailReport';
import Notifications from './pages/admin/Notifications';
import BatchesWithoutStudentsNotifications from './pages/admin/BatchesWithoutStudentsNotifications';
import StudentsWithoutImagesNotifications from './pages/admin/StudentsWithoutImagesNotifications';
import PathwayVerificationEligibleBatchesNotifications from './pages/admin/PathwayVerificationEligibleBatchesNotifications';
import PastStudentsNotGraduatedNotifications from './pages/admin/PastStudentsNotGraduatedNotifications';
import DelayedUniversityRegistrationsNotifications from './pages/admin/DelayedUniversityRegistrationsNotifications';
import UnscheduledSchedulesNotifications from './pages/admin/UnscheduledSchedulesNotifications';
import LowHoursSchedulesNotifications from './pages/admin/LowHoursSchedulesNotifications';
import StaleIssueNotifications from './pages/admin/StaleIssueNotifications';
import UnmarkedAttendanceNotifications from './pages/admin/UnmarkedAttendanceNotifications';
import AuthService from './services/authService';
import axiosInstance from './utils/axiosInstance';
import RedirectHandler from './components/RedirectHandler';

function RequireSuperAdmin({ children }) {
  const user = AuthService.getUser();
  const isSuperAdmin = Boolean(user?.superAdmin?.superAdminId);

  if (!isSuperAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

function RequirePlatformAdmin({ children }) {
  const user = AuthService.getUser();
  const isSuperAdmin = Boolean(user?.superAdmin?.superAdminId);
  const role = String(
    user?.superAdmin?.staff?.role ||
    user?.superAdmin?.role ||
    user?.superAdmin?.staff?.staffType ||
    user?.superAdmin?.staff?.type ||
    ''
  ).trim().toLowerCase();
  const isAdmin = isSuperAdmin || role === 'admin' || role === 'administrator';

  if (!isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

function RequireAdminAccess({ children }) {
  const [allowed, setAllowed] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      try {
        const stored = AuthService.getUser();
        if (!stored) {
          if (!cancelled) setAllowed(false);
          return;
        }

        const isSuperAdmin = Boolean(stored?.superAdmin?.superAdminId);
        if (isSuperAdmin) {
          if (!cancelled) setAllowed(true);
          return;
        }

        const resp = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (cancelled || !resp || !resp.data) {
          if (!cancelled) setAllowed(false);
          return;
        }

        const data = resp.data || {};
        const coordinateCourses = data.coordinateCourses || {};
        const accessibleCourseIds = Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : [];
        const coordinateCount = Object.keys(coordinateCourses).length || accessibleCourseIds.length;

        const staff = stored?.superAdmin?.staff || {};
        const categoryRaw = staff.category || staff.staffType || staff.type || '';
        const category = String(categoryRaw).toLowerCase();
        const isAcademicCategory = category === 'academic';
        const isSalesCategory = category === 'sales';
        const roleRaw = staff.role || '';
        const roleLower = String(roleRaw).toLowerCase().replace(/\s+/g, '');
        const staffTypeLower = String(staff.staffType || '').toLowerCase().replace(/\s+/g, '');
        const isRecoveryOfficer = staffTypeLower === 'recoveryofficer' || roleLower === 'recoveryofficer' || category === 'recovery';

        let canAccessAdmin = false;

        if (coordinateCount > 0) {
          // One or more coordinate courses: allow admin + lecturer routing for non-sales/recovery staff
          canAccessAdmin = !isSalesCategory && !isRecoveryOfficer;
        } else {
          // No coordinate courses:
          // - academic category     -> lecturer-only
          // - sales category        -> sales-only
          // - recovery officer      -> recovery-only
          // - others                -> allow admin
          if (isAcademicCategory || isSalesCategory || isRecoveryOfficer) {
            canAccessAdmin = false;
          } else {
            canAccessAdmin = true;
          }
        }

        if (!cancelled) setAllowed(canAccessAdmin);
      } catch (err) {
        if (!cancelled) setAllowed(false);
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, []);

  if (allowed === null) {
    return null;
  }

  if (!allowed) {
    const stored = AuthService.getUser();
    const staff = stored?.superAdmin?.staff || {};
    const categoryRaw = staff.category || staff.staffType || staff.type || '';
    const category = String(categoryRaw).toLowerCase();
    const isAcademicCategory = category === 'academic';
    const isSalesCategory = category === 'sales';
    const roleRaw = staff.role || '';
    const roleLower = String(roleRaw).toLowerCase().replace(/\s+/g, '');
    const staffTypeLower = String(staff.staffType || '').toLowerCase().replace(/\s+/g, '');
    const isRecoveryOfficer = staffTypeLower === 'recoveryofficer' || roleLower === 'recoveryofficer' || category === 'recovery';

    let redirectTo = '/login';
    if (isRecoveryOfficer) {
      redirectTo = '/recovery/dashboard';
    } else if (isSalesCategory) {
      redirectTo = '/sales/dashboard';
    } else if (isAcademicCategory) {
      redirectTo = '/lecturer/dashboard';
    }

    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

function RequireCourseAccess({ children, type }) {
  const [allowed, setAllowed] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      try {
        const resp = await axiosInstance.get('/admin/me/coordinate-courses');
        if (cancelled || !resp?.data) {
          if (!cancelled) setAllowed(false);
          return;
        }
        const data = resp.data;
        const isSuperAdmin = Boolean(data.isSuperAdmin);
        const hasHnd = Boolean(data.hasHndCourses);
        const hasTopup = Boolean(data.hasTopupCourses);

        // Normalize type so variants like "topup", "Top-Up", "TOP UP", "top up" all work
        const normalizedType = String(type)
          .replace(/[\s-]+/g, '')
          .toLowerCase();

        let can = false;
        if (normalizedType === 'hnd') {
          can = isSuperAdmin || hasHnd;
        } else if (normalizedType === 'topup') {
          can = isSuperAdmin || hasTopup;
        }

        setAllowed(can);
      } catch (err) {
        if (!cancelled) setAllowed(false);
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [type]);

  if (allowed === null) {
    return null;
  }

  if (!allowed) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

// Restrict access to Batch Groups page based on coordinate courses.
// Allowed if backend reports isSuperAdmin, or if any coordinateCourse
// matches one of the target HND course names.
function RequireBatchGroupsAccess({ children }) {
  const [allowed, setAllowed] = React.useState(null);

  React.useEffect(() => {
    let cancelled = false;

    const checkAccess = async () => {
      try {
        const resp = await axiosInstance.get('/admin/me/coordinate-courses');
        if (cancelled || !resp?.data) {
          if (!cancelled) setAllowed(false);
          return;
        }

        const data = resp.data || {};
        const isSuperAdmin = Boolean(data.isSuperAdmin);
        const coordinateCourses = data.coordinateCourses || {};

        // Mirror the HND (IT) detection used by the Delivery backend.
        const HND_NAMES = [
          'Level 5 Pearson BTEC HND in Computing (General)',
          'Level 5 Pearson BTEC HND in Computing (Software Engineering)',
          'Level 5 Pearson BTEC HND in Computing (Network Engineering)',
        ].map((v) => v.toLowerCase());

        const HND_LIKE_GROUPS = [
          ['hnd', 'computing'],
        ];

        const coordinateCourseList = Object.values(coordinateCourses || {});

        const hasHndItCourse = coordinateCourseList.some((course) => {
          if (!course) return false;
          const fullNameLower = String(course.fullName || course.courseName || '').toLowerCase().trim();
          const shortNameLower = String(course.shortName || course.courseShortName || '').toLowerCase().trim();

          const nameMatch = HND_NAMES.some((target) => {
            if (!target) return false;
            return (
              fullNameLower === target ||
              fullNameLower.includes(target) ||
              shortNameLower === target ||
              shortNameLower.includes(target)
            );
          });
          if (nameMatch) return true;

          const fields = [fullNameLower, shortNameLower];
          const likeMatch = HND_LIKE_GROUPS.some((tokens) =>
            tokens.every((token) => {
              const lower = token.toLowerCase();
              return fields.some((field) => field.includes(lower));
            })
          );

          return likeMatch;
        });

        const can = isSuperAdmin || hasHndItCourse;
        if (!cancelled) setAllowed(can);
      } catch (err) {
        if (!cancelled) setAllowed(false);
      }
    };

    checkAccess();

    return () => {
      cancelled = true;
    };
  }, []);

  if (allowed === null) {
    return null;
  }

  if (!allowed) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

function RequireLecturerAccess({ children }) {
  const stored = AuthService.getUser();

  if (!stored) {
    return <Navigate to="/login" replace />;
  }

  const staff = stored?.superAdmin?.staff || {};
  const categoryRaw = staff.category || staff.staffType || staff.type || '';
  const category = String(categoryRaw).toLowerCase();
  const isSalesCategory = category === 'sales';
  const roleRaw = staff.role || '';
  const roleLower = String(roleRaw).toLowerCase().replace(/\s+/g, '');
  const staffTypeLower = String(staff.staffType || '').toLowerCase().replace(/\s+/g, '');
  const isRecoveryOfficer = staffTypeLower === 'recoveryofficer' || roleLower === 'recoveryofficer' || category === 'recovery';

  if (isRecoveryOfficer) {
    return <Navigate to="/recovery/dashboard" replace />;
  }

  if (isSalesCategory) {
    return <Navigate to="/sales/dashboard" replace />;
  }

  return children;
}

function RequireRecoveryAccess({ children }) {
  const stored = AuthService.getUser();

  if (!stored) {
    return <Navigate to="/login" replace />;
  }

  const isSuperAdmin = Boolean(stored?.superAdmin?.superAdminId);
  const staff = stored?.superAdmin?.staff || {};
  const categoryRaw = staff.category || staff.staffType || staff.type || '';
  const category = String(categoryRaw).toLowerCase();
  const roleRaw = staff.role || '';
  const roleLower = String(roleRaw).toLowerCase().replace(/\s+/g, '');
  const staffTypeLower = String(staff.staffType || '').toLowerCase().replace(/\s+/g, '');
  const isRecoveryOfficer = staffTypeLower === 'recoveryofficer' || roleLower === 'recoveryofficer' || category === 'recovery';
  const isAdminRole = roleLower === 'admin' || roleLower === 'administrator' || staffTypeLower === 'admin' || staffTypeLower === 'administrator';
  const isManagementRole = roleLower === 'management' || roleLower === 'manager' || staffTypeLower === 'management' || staffTypeLower === 'manager';
  const canAccess = isRecoveryOfficer || isSuperAdmin || isAdminRole || isManagementRole;

  if (!canAccess) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function RequireAdminRole({ children }) {
  const stored = AuthService.getUser();

  if (!stored) {
    return <Navigate to="/login" replace />;
  }

  const userRole = stored?.superAdmin?.staff?.role || stored?.superAdmin?.role;
  const isSuperAdmin = Boolean(stored?.superAdmin?.superAdminId);
  const isAdmin = isSuperAdmin || userRole === 'Admin' || userRole === 'Administrator';

  if (!isAdmin) {
    return <Navigate to="/admin/dashboard" replace />;
  }

  return children;
}

function App() {
  return (
    <BrowserRouter>
      <RedirectHandler />
      <Routes>
        <Route path="/" element={<Navigate to="/login" replace />} />
        <Route path="/login" element={<Login />} />
        <Route
          path="/admin/dashboard"
          element={(
            <RequireAdminAccess>
              <Dashboard />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/students"
          element={(
            <RequireAdminAccess>
              <Students />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/my-course-due"
          element={(
            <RequireAdminAccess>
              <MyCourseDue />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/students/upload"
          element={(
            <RequireAdminAccess>
              <UploadStudents />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/students/university-registration-upload"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <UploadUniversityRegistrations />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/students/pathway-stream-upload"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <UploadPathwayStreams />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/staff-course-mapping-upload"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <UploadStaffCourseMappings />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/inactivated-inquiry"
          element={(
            <RequireAdminAccess>
              <InactivatedInquiry />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/inactivated-inquiry/by-assignee"
          element={(
            <RequireAdminAccess>
              <InactivatedInquiryByAssignee />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/students-without-registration"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <StudentsWithoutRegistration />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/report/student-detail"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <StudentDetailReport />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <Notifications />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/batches-without-students"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <BatchesWithoutStudentsNotifications />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/students-without-images"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <StudentsWithoutImagesNotifications />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/pathway-verification-eligible-batches"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <PathwayVerificationEligibleBatchesNotifications />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/past-students-not-graduated"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <PastStudentsNotGraduatedNotifications />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/delayed-university-registrations"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <DelayedUniversityRegistrationsNotifications />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/unscheduled-schedules"
          element={(
            <RequireAdminAccess>
              <UnscheduledSchedulesNotifications />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/low-hours-schedules"
          element={(
            <RequireAdminAccess>
              <LowHoursSchedulesNotifications />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/stale-issues"
          element={(
            <RequireAdminAccess>
              <StaleIssueNotifications />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/notifications/unmarked-attendance"
          element={(
            <RequireAdminAccess>
              <UnmarkedAttendanceNotifications />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/delivery"
          element={(
            <RequireAdminAccess>
              <Delivery />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/academic-calendar"
          element={(
            <RequireAdminAccess>
              <AcademicCalendar />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/correction"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <Correction />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/problems"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <Problems />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/contacts"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <Contacts />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/todo-list"
          element={(
            <RequireAdminAccess>
              <TodoList />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/student-issue"
          element={(
            <RequireAdminAccess>
              <StudentIssue />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/remider"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <Remider />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/sticky-note"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <StickyNote />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/hnd-exam-board"
          element={(
            <RequireAdminAccess>
              <RequireCourseAccess type="hnd">
                <HndExamBoard />
              </RequireCourseAccess>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/topup-exam-board"
          element={(
            <RequireAdminAccess>
              <RequireCourseAccess type="topup">
                <TopupExamBoard />
              </RequireCourseAccess>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/not-corrected-assignment-list"
          element={(
            <RequireAdminAccess>
              <NotCorrectedAssignmentList />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/hnd-assignment-tracking"
          element={(
            <RequireAdminAccess>
              <RequireCourseAccess type="hnd">
                <HndAssignmentTracking />
              </RequireCourseAccess>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/topup-assignment-tracking"
          element={(
            <RequireAdminAccess>
              <RequireCourseAccess type="topup">
                <TopupAssignmentTracking />
              </RequireCourseAccess>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/staff"
          element={(
            <RequireAdminAccess>
              <Staff />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/due-course-mapping"
          element={(
            <RequireAdminAccess>
              <RequirePlatformAdmin>
                <DueCourseMapping />
              </RequirePlatformAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/staff-unavailability"
          element={(
            <RequireAdminAccess>
              <StaffUnavailability />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/course-mapping"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <CourseMapping />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/schedule"
          element={(
            <RequireAdminAccess>
              <Timetable />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/attendance-tracking"
          element={(
            <RequireAdminAccess>
              <AttendanceTracking />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/lecture-hours"
          element={(
            <RequireAdminAccess>
              <LectureHours />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/students-attendance"
          element={(
            <RequireAdminAccess>
              <StudentsAttendance />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/positions"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <Positions />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/courses"
          element={(
            <RequireAdminAccess>
              <Courses />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/subjects"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <Subjects />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/assign-subject"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <AssignSubject />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/classroom"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <Classrooms />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/batches"
          element={(
            <RequireAdminAccess>
              <Batches />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/batch-groups"
          element={(
            <RequireAdminAccess>
              <RequireBatchGroupsAccess>
                <BatchGroups />
              </RequireBatchGroupsAccess>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/elms-module-code"
          element={(
            <RequireAdminAccess>
              <RequireSuperAdmin>
                <ElmsModuleCode />
              </RequireSuperAdmin>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin/profile"
          element={(
            <RequireAdminAccess>
              <Profile />
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/lecturer/dashboard"
          element={(
            <RequireLecturerAccess>
              <LecturerDashboard />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/lecturer/correction"
          element={(
            <RequireLecturerAccess>
              <LecturerCorrection />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/lecturer/attendance"
          element={(
            <RequireLecturerAccess>
              <Attendance />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/lecturer/my-feedback"
          element={(
            <RequireLecturerAccess>
              <MyFeedback />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/lecturer/support-task"
          element={(
            <RequireLecturerAccess>
              <SupportTask />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/lecturer/unavailability"
          element={(
            <RequireLecturerAccess>
              <LecturerUnavailability />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/lecturer/profile"
          element={(
            <RequireLecturerAccess>
              <LecturerProfile />
            </RequireLecturerAccess>
          )}
        />
        <Route
          path="/sales/inquiry"
          element={<SalesInquiry pageTitle="Student inquiries" section="inquiry" />}
        />
        <Route
          path="/sales/target"
          element={<SalesTarget pageTitle="Counselor targets" section="target" />}
        />
        <Route
          path="/sales/course-info"
          element={<SalesCourseInfo pageTitle="Course info" section="course-info" />}
        />
        <Route
          path="/sales/offer-letter"
          element={<SalesOfferLetter pageTitle="Offer letter" section="offer-letter" />}
        />
        <Route
          path="/sales/performance"
          element={<SalesPerformance pageTitle="Performance" section="performance" />}
        />
        <Route
          path="/sales/my-work"
          element={<SalesMyWork pageTitle="My work" section="my-work" />}
        />
        <Route
          path="/sales/profile"
          element={<SalesProfile pageTitle="Profile" section="profile" />}
        />
        <Route path="/sales/dashboard" element={<SalesDashboard />} />
        <Route
          path="/recovery/dashboard"
          element={
            <RequireRecoveryAccess>
              <RecoveryDashboard pageTitle="Dashboard" section="dashboard" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/recovery/arrears"
          element={
            <RequireRecoveryAccess>
              <RecoveryArrears pageTitle="Arrears" section="arrears" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/recovery/payments"
          element={
            <RequireRecoveryAccess>
              <RecoveryPayments pageTitle="Payments" section="payments" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/recovery/dues"
          element={
            <RequireRecoveryAccess>
              <RecoveryDuesList pageTitle="Dues List" section="dues" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/recovery/upload-dues"
          element={
            <RequireRecoveryAccess>
              <RecoveryUploadDues pageTitle="Upload Dues" section="dues" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/recovery/profile"
          element={
            <RequireRecoveryAccess>
              <RecoveryProfile pageTitle="Profile" section="profile" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/recovery/inquiry-followup"
          element={
            <RequireRecoveryAccess>
              <InactivatedInquiryFollowup pageTitle="Inactivated List Followup" section="inquiry-followup" />
            </RequireRecoveryAccess>
          }
        />
        <Route
          path="/admin-sales/dashboard"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesDashboard />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/inquiries"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesInquiries />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/team-targets/course-code"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesCourseCode />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/team-targets/coordinator-target"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesStaffCourseMapping />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/team-targets/summary"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesTargetSummary />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/branch-targets/course-category"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesCourseCategory />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/branch-targets/annual-target"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesAnnualTarget />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/branch-targets/monthly-target"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesBranchMonthlyTarget />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/branch-targets/new-income"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesBranchNewIncome />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route
          path="/admin-sales/branch-targets/new-income-analysis"
          element={(
            <RequireAdminAccess>
              <RequireAdminRole>
                <AdminSalesBranchNewIncomeAnalysis />
              </RequireAdminRole>
            </RequireAdminAccess>
          )}
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
